import React, { useRef, useState, useEffect, useMemo } from "react";
const MIN_HEIGHT = 340;
const MESSAGE_TYPE = "conteudo-html-height";

// Hash simples e estável (djb2) do conteúdo HTML do bloco: usado para compor
// a "versão" do material dentro da chave de armazenamento. Quando o admin
// edita as perguntas/alternativas de um material, o HTML muda, o hash muda,
// e o progresso salvo sob a versão antiga deixa de ser lido — sem precisar
// de um campo de versão dedicado no backend.
function hashContent(value) {
  const str = String(value || "");
  let hash = 5381;
  for (let index = 0; index < str.length; index++) {
    hash = (hash * 33) ^ str.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
}
function buildBootstrapScript(namespace) {
  return `
<script>
(() => {
  const PREFIX = "dmiso1:" + ${JSON.stringify(namespace)} + "::";

  const createMemoryStorage = () => {
    const store = new Map();

    return {
      get length() {
        return store.size;
      },
      clear() {
        store.clear();
      },
      getItem(key) {
        const normalizedKey = String(key);
        return store.has(normalizedKey) ? store.get(normalizedKey) : null;
      },
      key(index) {
        return Array.from(store.keys())[index] ?? null;
      },
      removeItem(key) {
        store.delete(String(key));
      },
      setItem(key, value) {
        store.set(String(key), String(value));
      },
    };
  };

  // Envolve o storage real (compartilhado pela origem do site) prefixando
  // toda chave com o namespace desta instância (aluno+aula+guia+bloco+versão).
  // Assim leitura, gravação, exclusão e "clear()" nunca alcançam dados de
  // outro aluno, outra aula/atividade, ou chaves do próprio site (ex.:
  // sessão de login), e chaves antigas sem prefixo (salvas antes desta
  // correção) deixam de ser enxergadas — não há import automático delas.
  const createNamespacedStorage = (realStorage) => {
    const hasPrefix = (key) => typeof key === "string" && key.indexOf(PREFIX) === 0;
    const stripPrefix = (key) => key.slice(PREFIX.length);
    const namespacedKeys = () => {
      const keys = [];
      for (let i = 0; i < realStorage.length; i++) {
        const key = realStorage.key(i);
        if (hasPrefix(key)) keys.push(key);
      }
      return keys;
    };

    return {
      get length() {
        return namespacedKeys().length;
      },
      clear() {
        namespacedKeys().forEach((key) => realStorage.removeItem(key));
      },
      getItem(key) {
        return realStorage.getItem(PREFIX + String(key));
      },
      key(index) {
        return stripPrefix(namespacedKeys()[index] ?? "") || null;
      },
      removeItem(key) {
        realStorage.removeItem(PREFIX + String(key));
      },
      setItem(key, value) {
        realStorage.setItem(PREFIX + String(key), String(value));
      },
    };
  };

  const installNamespacedStorage = (storageName) => {
    let real = null;

    try {
      real = window[storageName];
      const probeKey = "__conteudo_probe__";
      real.setItem(probeKey, "1");
      real.removeItem(probeKey);
    } catch {
      real = null;
    }

    const namespaced = real
      ? createNamespacedStorage(real)
      : createMemoryStorage();

    try {
      Object.defineProperty(window, storageName, {
        configurable: true,
        enumerable: true,
        get() {
          return namespaced;
        },
      });
    } catch {
      try {
        Object.defineProperty(window, storageName, {
          configurable: true,
          enumerable: true,
          writable: false,
          value: namespaced,
        });
      } catch {}
    }
  };

  installNamespacedStorage("localStorage");
  installNamespacedStorage("sessionStorage");
})();
</script>`;
}
function buildResizeScript(frameId) {
  return `
<script>
(() => {
  const frameId = ${JSON.stringify(frameId)};
  const postHeight = () => {
    const body = document.body;
    const doc = document.documentElement;
    const height = Math.max(
      body ? body.scrollHeight : 0,
      body ? body.offsetHeight : 0,
      doc ? doc.scrollHeight : 0,
      doc ? doc.offsetHeight : 0,
      ${MIN_HEIGHT}
    );

    window.parent.postMessage({
      type: ${JSON.stringify(MESSAGE_TYPE)},
      frameId,
      height,
    }, "*");
  };

  window.addEventListener("load", postHeight);
  window.addEventListener("resize", postHeight);
  document.addEventListener("click", () => setTimeout(postHeight, 0), true);
  document.addEventListener("input", () => setTimeout(postHeight, 0), true);

  const observer = new MutationObserver(() => postHeight());
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    characterData: true,
  });

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(postHeight).catch(() => {});
  }

  setTimeout(postHeight, 0);
  setTimeout(postHeight, 150);
  setTimeout(postHeight, 500);
})();
</script>`;
}
function buildSrcDoc(html, frameId, namespace) {
  const bootstrapScript = buildBootstrapScript(namespace);
  const resizeScript = buildResizeScript(frameId);
  let content =
    html +
    '<style data-dm-mobile-gutters>@media(max-width:600px){[id^="dm-"][id$="-wrapper"] .p-6,[id^="dm-"][id$="-wrapper"] .p-8,[id^="dm-"][id$="-wrapper"] .px-6,[id^="dm-"][id$="-wrapper"] .px-8{padding-left:8px!important;padding-right:8px!important}[id^="dm-"][id$="-wrapper"] .border-l-4.pl-6{padding-left:8px!important}}</style>';
  content = /<head[^>]*>/i.test(content)
    ? content.replace(/<head([^>]*)>/i, "<head$1>".concat(bootstrapScript))
    : /<html[^>]*>/i.test(content)
      ? content.replace(/<html([^>]*)>/i, "<html$1>".concat(bootstrapScript))
      : "".concat(bootstrapScript).concat(content);
  return /<\/body>/i.test(content)
    ? content.replace(/<\/body>/i, "".concat(resizeScript, "</body>"))
    : /<\/html>/i.test(content)
      ? content.replace(/<\/html>/i, "".concat(resizeScript, "</html>"))
      : '<!DOCTYPE html>\n<html lang="pt-BR">\n  <head>\n    <meta charset="utf-8" />\n    <meta name="viewport" content="width=device-width, initial-scale=1" />\n    <style>\n      html, body {\n        margin: 0;\n        padding: 0;\n        background: transparent;\n      }\n\n      body {\n        box-sizing: border-box;\n      }\n\n      *, *::before, *::after {\n        box-sizing: border-box;\n      }\n\n      img, iframe, video, canvas, svg {\n        max-width: 100%;\n      }\n    </style>\n  </head>\n  <body>\n    '
          .concat(content, "\n    ")
          .concat(resizeScript, "\n  </body>\n</html>");
}
export function HtmlContentFrame(props) {
  const html = props.html || "";
  const instance = props.instanceKey || "default";
  const min = props.minHeight || 340;
  const ref = useRef(null);
  const frameId = useRef("dm-frame-" + crypto.randomUUID());
  const prepared = useState(null);
  const content = prepared[0];
  const setContent = prepared[1];
  const height = useState(min);
  const size = height[0];
  const setSize = height[1];
  const issue = useState("");
  const problem = issue[0];
  const setProblem = issue[1];
  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    let session = null;
    setContent(null);
    setSize(min);
    setProblem("");
    window.DMLearning.prepare(
      html,
      instance,
      controller.signal,
      props.contentKind,
    )
      .then((result) => {
        session = result.session;
        if (!live) {
          session?.dispose();
          return;
        }
        setContent(result);
      })
      .catch((err) => {
        if (live && err.name !== "AbortError") setProblem(err.message);
      });
    return () => {
      live = false;
      controller.abort();
      session?.dispose();
    };
  }, [html, instance, props.contentKind]);
  const src = useMemo(
    () =>
      content
        ? buildSrcDoc(
            window.DMLearning.doc(
              content.html,
              content.base || window.location.href,
              frameId.current,
              content.session,
            ),
            frameId.current,
            instance,
          )
        : "",
    [content, instance],
  );
  useEffect(() => {
    if (!content || !ref.current) return;
    return window.DMLearning.bindFrame(
      ref.current,
      frameId.current,
      content.session,
    );
  }, [content]);
  useEffect(() => {
    const listener = (response) => {
      if (
        response.source !== ref.current?.contentWindow ||
        response.data?.type !== MESSAGE_TYPE ||
        response.data?.frameId !== frameId.current
      )
        return;
      const nextHeight = Number(response.data.height);
      if (Number.isFinite(nextHeight) && nextHeight > 0)
        setSize(Math.max(min, Math.min(250000, nextHeight)));
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [min]);
  if (!html) return null;
  if (!content)
    return (
      <div
        role={"status"}
        style={{
          padding: 12,
          color: "#526273",
          fontSize: 13,
        }}
      >
        {problem || "Carregando atividade…"}
      </div>
    );
  return (
    <iframe
      ref={ref}
      key={instance + ":" + content.session?.c.revision}
      title={props.title || "Conteúdo HTML"}
      srcDoc={src}
      scrolling={"no"}
      sandbox={
        "allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-same-origin"
      }
      style={{
        width: "100%",
        minHeight: min,
        height: size,
        border: "none",
        display: "block",
        background: "transparent",
      }}
    />
  );
}
